import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import './css/Statistics.css';
import axios from 'axios';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { parseLocalDate, formatLocalDateTime } from './common/utils';
import { toast } from 'react-toastify';
import { useGoals } from './contexts/GoalContext';
import { useTimeTable } from './contexts/TimeTableContext';
import { useStreak } from './contexts/StreakContext';
import { useAuth } from './common/AuthContext';
import { authFetch } from './common/authFetch';
import ShareDialog from './components/ShareDialog';
import ConfirmationDialog from './ConfirmationDialog';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import { downloadImage, canvasToBlob } from './common/shareService';

const Statistics = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const userId = new URLSearchParams(location.search).get('userId');
  const isReadOnly = !!userId;

  // Observer: student selector
  const [myStudents, setMyStudents] = useState([]);
  const handleStudentChange = (e) => {
    const newUserId = e.target.value;
    navigate(newUserId ? `/statistics?userId=${newUserId}` : '/statistics');
  };
  const [timeRange, setTimeRange] = useState('day'); // 'day', 'week', 'month', 'all'
  const [studyData, setStudyData] = useState([]);
  const [totalHours, setTotalHours] = useState(0);
  const [loading, setLoading] = useState(false);
  const [displayPeriod, setDisplayPeriod] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [viewMode, setViewMode] = useState('studyTime'); // 'studyTime', 'achievement', 'goals', 'streaks'
  const [achievementData, setAchievementData] = useState([]);
  const [todoAchievementData, setTodoAchievementData] = useState([]);

  // Goal states
  const { goals, progress, goalLoading, fetchGoals, fetchProgress, fetchAllProgress, createGoal, updateGoal, deleteGoal } = useGoals();
  const { subjects, startWithMonday } = useTimeTable();
  const { streak, badges, streakLoading, fetchStreak, fetchBadges } = useStreak();
  const { user } = useAuth();
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [shareCardType, setShareCardType] = useState('daily');
  const [shareCardData, setShareCardData] = useState({});
  const [goalDialogOpen, setGoalDialogOpen] = useState(false);
  const [editingGoal, setEditingGoal] = useState(null);
  const [goalForm, setGoalForm] = useState({ period_type: 'weekly', target_hours: '', subject_id: '', start_date: '', end_date: '', description: '' });
  const [deleteConfirmGoalId, setDeleteConfirmGoalId] = useState(null);
  const [goalPeriodType, setGoalPeriodType] = useState('all');

  // Print / Export states
  const printRef = useRef(null);
  const [isPdfExporting, setIsPdfExporting] = useState(false);
  const [pdfPreview, setPdfPreview] = useState(null);
  const [isPngExporting, setIsPngExporting] = useState(false);
  const [pngPreview, setPngPreview] = useState(null);

  // Actual Study states
  const [actualStudySessions, setActualStudySessions] = useState([]);
  const [completedTodos, setCompletedTodos] = useState([]);
  const [deleteSessionConfirm, setDeleteSessionConfirm] = useState(null);
  const [editingSessionId, setEditingSessionId] = useState(null);
  const [editHours, setEditHours] = useState('');
  const [editMinutes, setEditMinutes] = useState('');

  // iOS Safari: 모달 열릴 때 배경 스크롤 방지
  useEffect(() => {
    const anyDialogOpen = goalDialogOpen || deleteConfirmGoalId;
    if (anyDialogOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [goalDialogOpen, deleteConfirmGoalId]);

  useEffect(() => {
    const fetchMyStudents = async () => {
      if (!user) return;
      try {
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/v1/observer/students`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('accessToken')}` }
        });
        const data = await res.json();
        if (data.success) setMyStudents(data.students);
      } catch (e) { console.error("Failed to fetch students", e); }
    };
    fetchMyStudents();
  }, [user]);

  useEffect(() => {
    // API로부터 데이터를 가져오는 함수
    fetchStudyData(timeRange);
  }, [timeRange, userId]);

  const isLightColor = (color) => {
    if (!color) return false;
    const hex = color.replace('#', '');
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    const brightness = ((r * 299) + (g * 587) + (b * 114)) / 1000;
    return brightness > 200; // Threshold for "light" color
  };

  const fetchStudyData = async (range) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('accessToken');
      if (!token) return;

      let start, end;
      const now = new Date();

      // Date Range Calculation
      if (range === 'all') {
        start = new Date(now.getFullYear(), 0, 1, 0, 0, 0);
        end = new Date();
        end.setHours(23, 59, 59, 999);
      } else if (range === 'day') {
        start = new Date(now.setHours(0, 0, 0, 0));
        end = new Date(now.setHours(23, 59, 59, 999));
      } else if (range === 'week') {
        const dayOfWeek = now.getDay(); // 0=Sun, 1=Mon, ...
        if (startWithMonday) {
          // 월요일 시작: Mon~Sun
          const diff = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
          start = new Date(now.getFullYear(), now.getMonth(), now.getDate() - diff, 0, 0, 0, 0);
        } else {
          // 일요일 시작: Sun~Sat
          start = new Date(now.getFullYear(), now.getMonth(), now.getDate() - dayOfWeek, 0, 0, 0, 0);
        }
        end = new Date(start.getTime() + 6 * 24 * 60 * 60 * 1000);
        end.setHours(23, 59, 59, 999);
      } else if (range === 'month') {
        start = new Date(now.getFullYear(), now.getMonth(), 1);
        end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
        end.setHours(23, 59, 59, 999);
      } else if (range === 'custom') {
        if (!startDate || !endDate) return;
        start = parseLocalDate(startDate);
        start.setHours(0, 0, 0, 0);
        end = parseLocalDate(endDate);
        end.setHours(23, 59, 59, 999);
      }

      // Fetch Raw Data
      // Format Period for Display
      const formatDate = (d) => `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, '0')}.${String(d.getDate()).padStart(2, '0')}`;
      setDisplayPeriod(`${formatDate(start)} ~ ${formatDate(end)}`);


      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/v1/time_table`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          startDate: formatLocalDateTime(start),
          endDate: formatLocalDateTime(end),
          userId: userId
        }
      });

      if (response.data.success) {
        processAndSetData(response.data.schedules);
      }
    } catch (error) {
      console.error('Failed to fetch statistics:', error);
    } finally {
      setLoading(false);
    }
  };

  const processAndSetData = (schedules) => {
    const categoryMap = {};
    let totalTime = 0;

    if (!schedules || schedules.length === 0) {
      setStudyData([]);
      setTotalHours(0);
      return;
    }

    schedules.forEach(schedule => {
      // Safe access to nested properties
      const subjectObj = schedule.study_subject || schedule.StudySubject || {};
      const categoryObj = subjectObj.category || (subjectObj.Category ? subjectObj.Category : {});

      const subjectName = subjectObj.subjectname || 'Unknown';
      const color = subjectObj.color || '#ccc';
      const categoryName = categoryObj.category_name || t('statistics.category.uncategorized') || 'Uncategorized';
      const categoryId = categoryObj.category_id || 'uncategorized';
      const categoryColor = categoryObj.color || color; // Use category color if available, else subject color

      const time = schedule.scheduled_time || 0;
      const subjectId = schedule.subject_id;

      // Group by Category
      if (!categoryMap[categoryId]) {
        categoryMap[categoryId] = {
          id: categoryId,
          name: categoryName,
          totalHours: 0,
          subjects: {},
          color: categoryColor
        };
      }

      categoryMap[categoryId].totalHours += time;

      // Group by Subject within Category
      if (!categoryMap[categoryId].subjects[subjectId]) {
        categoryMap[categoryId].subjects[subjectId] = {
          name: subjectName,
          hours: 0,
          color: color
        };
      }
      categoryMap[categoryId].subjects[subjectId].hours += time;

      totalTime += time;
    });

    // Convert Map to Array for rendering
    const categoryList = Object.values(categoryMap).map(cat => ({
      name: cat.name,
      color: cat.color,
      totalHours: parseFloat((cat.totalHours / 60).toFixed(1)),
      subjects: Object.values(cat.subjects).map(sub => ({
        name: sub.name,
        hours: parseFloat((sub.hours / 60).toFixed(1)),
        color: sub.color
      })).sort((a, b) => b.hours - a.hours)
    }));

    // Sort categories by total hours desc
    categoryList.sort((a, b) => b.totalHours - a.totalHours);

    setStudyData(categoryList);
    setTotalHours(parseFloat((totalTime / 60).toFixed(1)));
  };



  const fetchAchievementData = async (range) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('accessToken');
      if (!token) return;

      let start, end;
      const now = new Date();

      if (range === 'all') {
        start = new Date(now.getFullYear(), 0, 1, 0, 0, 0);
        end = new Date();
        end.setHours(23, 59, 59, 999);
      } else if (range === 'day') {
        start = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0);
        end = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
      } else if (range === 'week') {
        const dayOfWeek = now.getDay();
        if (startWithMonday) {
          const diff = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
          start = new Date(now.getFullYear(), now.getMonth(), now.getDate() - diff, 0, 0, 0, 0);
        } else {
          start = new Date(now.getFullYear(), now.getMonth(), now.getDate() - dayOfWeek, 0, 0, 0, 0);
        }
        end = new Date(start.getTime() + 6 * 24 * 60 * 60 * 1000);
        end.setHours(23, 59, 59, 999);
      } else if (range === 'month') {
        start = new Date(now.getFullYear(), now.getMonth(), 1);
        end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
        end.setHours(23, 59, 59, 999);
      } else if (range === 'custom') {
        if (!startDate || !endDate) return;
        start = parseLocalDate(startDate);
        start.setHours(0, 0, 0, 0);
        end = parseLocalDate(endDate);
        end.setHours(23, 59, 59, 999);
      }


      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/v1/study_sessions/summary`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          startDate: formatLocalDateTime(start),
          endDate: formatLocalDateTime(end),
          userId: userId
        }
      });

      if (response.data.success) {
        const summary = response.data.summary || {};
        setAchievementData(summary.schedule || []);
        setTodoAchievementData(summary.todo || []);
      }

      const formatDate = (d) => `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, '0')}.${String(d.getDate()).padStart(2, '0')}`;
      setDisplayPeriod(`${formatDate(start)} ~ ${formatDate(end)}`);
    } catch (error) {
      console.error('Failed to fetch achievement data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (viewMode === 'achievement') {
      fetchAchievementData(timeRange);
    }
  }, [viewMode, timeRange, userId]);

  // Actual Study fetch
  const fetchActualStudyData = async (range) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('accessToken');
      if (!token) return;

      let start, end;
      const now = new Date();

      if (range === 'all') {
        start = new Date(now.getFullYear(), 0, 1, 0, 0, 0);
        end = new Date(); end.setHours(23, 59, 59, 999);
      } else if (range === 'day') {
        start = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0);
        end = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
      } else if (range === 'week') {
        const dayOfWeek = now.getDay();
        if (startWithMonday) {
          const diff = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
          start = new Date(now.getFullYear(), now.getMonth(), now.getDate() - diff, 0, 0, 0, 0);
        } else {
          start = new Date(now.getFullYear(), now.getMonth(), now.getDate() - dayOfWeek, 0, 0, 0, 0);
        }
        end = new Date(start.getTime() + 6 * 24 * 60 * 60 * 1000);
        end.setHours(23, 59, 59, 999);
      } else if (range === 'month') {
        start = new Date(now.getFullYear(), now.getMonth(), 1);
        end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
        end.setHours(23, 59, 59, 999);
      } else if (range === 'custom') {
        if (!startDate || !endDate) return;
        start = parseLocalDate(startDate); start.setHours(0, 0, 0, 0);
        end = parseLocalDate(endDate); end.setHours(23, 59, 59, 999);
      }


      // Fetch study sessions
      const sessionsRes = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/v1/study_sessions`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { startDate: formatLocalDateTime(start), endDate: formatLocalDateTime(end), userId }
      });
      if (sessionsRes.data.success) {
        setActualStudySessions(sessionsRes.data.sessions || []);
      }

      // Fetch completed TODOs from time_table
      const ttRes = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/v1/time_table`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { startDate: formatLocalDateTime(start), endDate: formatLocalDateTime(end), userId }
      });
      if (ttRes.data.success) {
        const todos = (ttRes.data.schedules || []).filter(s => s.schedule_type === 'todo' && s.completed);
        setCompletedTodos(todos);
      }

      const formatDate = (d) => `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, '0')}.${String(d.getDate()).padStart(2, '0')}`;
      setDisplayPeriod(`${formatDate(start)} ~ ${formatDate(end)}`);
    } catch (error) {
      console.error('Failed to fetch actual study data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (viewMode === 'actualStudy') {
      fetchActualStudyData(timeRange);
    }
  }, [viewMode, timeRange, userId]);

  const handleDeleteSession = async (session) => {
    try {
      const res = await authFetch(`${process.env.REACT_APP_BACKEND_URL}/api/v1/study_sessions/${session.session_id}`, {
        method: 'DELETE'
      });
      const data = await res.json();
      if (data.success) {
        toast.success(t('actualStudy.deleted'));
        fetchActualStudyData(timeRange);
      }
    } catch (error) {
      console.error('Failed to delete session:', error);
    } finally {
      setDeleteSessionConfirm(null);
    }
  };

  const startEditSession = (session) => {
    const displaySec = session.modified_seconds != null ? session.modified_seconds : session.total_seconds;
    setEditingSessionId(session.session_id);
    setEditHours(String(Math.floor(displaySec / 3600)));
    setEditMinutes(String(Math.floor((displaySec % 3600) / 60)));
  };

  const cancelEditSession = () => {
    setEditingSessionId(null);
    setEditHours('');
    setEditMinutes('');
  };

  const saveEditSession = async (sessionId) => {
    const h = parseInt(editHours, 10) || 0;
    const m = parseInt(editMinutes, 10) || 0;
    const modifiedSeconds = h * 3600 + m * 60;
    try {
      const res = await authFetch(`${process.env.REACT_APP_BACKEND_URL}/api/v1/study_sessions/${sessionId}/modified-seconds`, {
        method: 'PUT',
        body: JSON.stringify({ modified_seconds: modifiedSeconds })
      });
      const data = await res.json();
      if (data.success) {
        toast.success(t('actualStudy.timeUpdated'));
        fetchActualStudyData(timeRange);
      }
    } catch (error) {
      console.error('Failed to update session time:', error);
    } finally {
      cancelEditSession();
    }
  };

  const resetEditSession = async (sessionId) => {
    try {
      const res = await authFetch(`${process.env.REACT_APP_BACKEND_URL}/api/v1/study_sessions/${sessionId}/modified-seconds`, {
        method: 'PUT',
        body: JSON.stringify({ modified_seconds: null })
      });
      const data = await res.json();
      if (data.success) {
        toast.success(t('actualStudy.timeReset'));
        fetchActualStudyData(timeRange);
      }
    } catch (error) {
      console.error('Failed to reset session time:', error);
    } finally {
      cancelEditSession();
    }
  };

  const handleUndoTodoComplete = async (todo) => {
    try {
      await authFetch(`${process.env.REACT_APP_BACKEND_URL}/api/v1/time_table/todo/toggle`, {
        method: 'PUT',
        body: JSON.stringify({ schedule_id: todo.schedule_id, completed: false })
      });
      toast.success(t('actualStudy.undoComplete'));
      fetchActualStudyData(timeRange);
    } catch (error) {
      console.error('Failed to undo todo:', error);
    }
  };

  // Goal effects
  useEffect(() => {
    if (viewMode === 'goals') {
      fetchGoals();
      if (goalPeriodType === 'all') {
        fetchAllProgress();
      } else {
        const today = new Date().toISOString().split('T')[0];
        fetchProgress(goalPeriodType, today);
      }
    }
  }, [viewMode, goalPeriodType, fetchGoals, fetchProgress]);

  // Streak effects
  useEffect(() => {
    if (viewMode === 'streaks') {
      fetchStreak();
      fetchBadges();
    }
  }, [viewMode, fetchStreak, fetchBadges]);

  const BADGE_DEFINITIONS = [
    { badge_key: 'streak_7', icon: '\uD83D\uDD25', nameKey: 'streak.streak7', descKey: 'streak.streak7Desc' },
    { badge_key: 'streak_30', icon: '\uD83D\uDD25', nameKey: 'streak.streak30', descKey: 'streak.streak30Desc' },
    { badge_key: 'streak_100', icon: '\uD83D\uDD25', nameKey: 'streak.streak100', descKey: 'streak.streak100Desc' },
    { badge_key: 'total_100h', icon: '\u23F0', nameKey: 'streak.total100h', descKey: 'streak.total100hDesc' },
    { badge_key: 'total_500h', icon: '\u23F0', nameKey: 'streak.total500h', descKey: 'streak.total500hDesc' },
    { badge_key: 'total_1000h', icon: '\u23F0', nameKey: 'streak.total1000h', descKey: 'streak.total1000hDesc' },
    { badge_key: 'daily_5h', icon: '\u2B50', nameKey: 'streak.daily5h', descKey: 'streak.daily5hDesc' },
    { badge_key: 'daily_10h', icon: '\u2B50', nameKey: 'streak.daily10h', descKey: 'streak.daily10hDesc' },
  ];

  const handleGoalPeriodChange = (period) => {
    setGoalPeriodType(period);
  };

  const openGoalDialog = (goal = null) => {
    if (goal) {
      setEditingGoal(goal);
      setGoalForm({
        period_type: goal.period_type,
        target_hours: goal.target_hours,
        subject_id: goal.subject_id || '',
        start_date: goal.start_date || '',
        end_date: goal.end_date || '',
        description: goal.description || ''
      });
    } else {
      setEditingGoal(null);
      setGoalForm({ period_type: 'weekly', target_hours: '', subject_id: '', start_date: '', end_date: '', description: '' });
    }
    setGoalDialogOpen(true);
  };

  const closeGoalDialog = () => {
    setGoalDialogOpen(false);
    setEditingGoal(null);
    setGoalForm({ period_type: 'weekly', target_hours: '', subject_id: '', start_date: '', end_date: '', description: '' });
  };

  const handleGoalSubmit = async () => {
    if (!goalForm.target_hours || parseFloat(goalForm.target_hours) <= 0) {
      toast.error(t('fillAllFields'));
      return;
    }
    if (goalForm.period_type === 'custom' && (!goalForm.start_date || !goalForm.end_date)) {
      toast.error(t('fillAllFields'));
      return;
    }
    if (goalForm.period_type === 'custom' && goalForm.start_date >= goalForm.end_date) {
      toast.error(t('fillAllFields'));
      return;
    }

    const payload = {
      period_type: goalForm.period_type,
      target_hours: parseFloat(goalForm.target_hours),
      subject_id: goalForm.subject_id || null
    };
    if (goalForm.period_type === 'custom') {
      payload.start_date = goalForm.start_date;
      payload.end_date = goalForm.end_date;
    }
    if (goalForm.description) {
      payload.description = goalForm.description;
    }

    if (editingGoal) {
      await updateGoal(editingGoal.goal_id, {
        target_hours: payload.target_hours,
        description: goalForm.description || null,
        is_active: true
      });
    } else {
      await createGoal(payload);
    }

    closeGoalDialog();
    if (goalPeriodType === 'all') {
      fetchAllProgress();
    } else {
      const today = new Date().toISOString().split('T')[0];
      fetchProgress(goalPeriodType, today);
    }
  };

  const handleDeleteGoal = async (goalId) => {
    await deleteGoal(goalId);
    setDeleteConfirmGoalId(null);
    if (goalPeriodType === 'all') {
      fetchAllProgress();
    } else {
      const today = new Date().toISOString().split('T')[0];
      fetchProgress(goalPeriodType, today);
    }
  };

  const getProgressColor = (percentage) => {
    if (percentage >= 100) return '#4caf50';
    if (percentage >= 70) return '#8bc34a';
    if (percentage >= 40) return '#ff9800';
    return '#f44336';
  };

  const getProgressGradient = (percentage) => {
    const clamped = Math.min(percentage, 100);
    if (clamped >= 100) return '#4caf50';
    if (clamped >= 70) return '#8bc34a';
    if (clamped >= 40) return '#ff9800';
    return '#f44336';
  };

  const getPeriodLabel = (periodType) => {
    switch (periodType) {
      case 'daily': return t('goals.periodDaily');
      case 'weekly': return t('goals.periodWeekly');
      case 'monthly': return t('goals.periodMonthly');
      case 'custom': return t('goals.periodCustom');
      default: return periodType;
    }
  };

  // PDF Export
  const handleExportPdf = async () => {
    if (!printRef.current || isPdfExporting) return;
    setIsPdfExporting(true);
    try {
      const canvas = await html2canvas(printRef.current, {
        scale: 2,
        useCORS: true,
        backgroundColor: getComputedStyle(document.documentElement).getPropertyValue('--card-bg').trim() || '#ffffff',
        logging: false,
      });
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 10;
      const ratio = Math.min((pageWidth - margin * 2) / canvas.width, (pageHeight - margin * 2) / canvas.height);
      const pdfW = canvas.width * ratio;
      const pdfH = canvas.height * ratio;
      pdf.addImage(imgData, 'PNG', (pageWidth - pdfW) / 2, margin, pdfW, pdfH);
      const fileName = `statistics_${viewMode}_${timeRange}.pdf`;
      setPdfPreview({ imgDataUrl: imgData, pdfInstance: pdf, fileName });
    } catch (e) { console.error('PDF export failed:', e); }
    finally { setIsPdfExporting(false); }
  };
  const handlePdfSave = () => { if (pdfPreview) { pdfPreview.pdfInstance.save(pdfPreview.fileName); setPdfPreview(null); } };

  // PNG Export
  const handleExportPng = async () => {
    if (!printRef.current || isPngExporting) return;
    setIsPngExporting(true);
    try {
      const canvas = await html2canvas(printRef.current, {
        scale: 2,
        useCORS: true,
        backgroundColor: getComputedStyle(document.documentElement).getPropertyValue('--card-bg').trim() || '#ffffff',
        logging: false,
      });
      const blob = await canvasToBlob(canvas);
      const imgDataUrl = canvas.toDataURL('image/png');
      const fileName = `statistics_${viewMode}_${timeRange}.png`;
      setPngPreview({ imgDataUrl, blob, fileName });
    } catch (e) { console.error('PNG export failed:', e); }
    finally { setIsPngExporting(false); }
  };
  const handlePngSave = () => { if (pngPreview) { downloadImage(pngPreview.blob, pngPreview.fileName); setPngPreview(null); } };

  // Share (capture printRef as image)
  const handleShareImage = async () => {
    if (!printRef.current) return;
    try {
      const canvas = await html2canvas(printRef.current, {
        scale: 2, useCORS: true,
        backgroundColor: getComputedStyle(document.documentElement).getPropertyValue('--card-bg').trim() || '#ffffff',
        logging: false,
      });
      const imageDataUrl = canvas.toDataURL('image/png');
      const label = `${t('statistics.title')} - ${viewMode}`;
      openShareDialog('timetable', { imageDataUrl, label, date: new Date().toLocaleDateString(), username: user?.username || '' });
    } catch (e) { console.error('Share capture failed:', e); }
  };

  const openShareDialog = (cardType, overrideData) => {
    if (overrideData) {
      setShareCardData(overrideData);
      setShareCardType(cardType);
      setShareDialogOpen(true);
      return;
    }
    const today = new Date();
    const dateStr = `${today.getFullYear()}.${String(today.getMonth() + 1).padStart(2, '0')}.${String(today.getDate()).padStart(2, '0')}`;

    if (cardType === 'daily' || cardType === 'weekly') {
      // Build subject list from achievementData (study_sessions summary) if available, or studyData
      let subjectList = [];
      let total = 0;
      if (achievementData.length > 0) {
        subjectList = achievementData.map(item => ({
          name: item.subject_name,
          color: item.subject_color,
          seconds: item.actual_seconds
        }));
        total = subjectList.reduce((sum, s) => sum + s.seconds, 0);
      } else {
        subjectList = studyData.flatMap(cat =>
          cat.subjects.map(sub => ({
            name: sub.name,
            color: sub.color,
            seconds: sub.hours * 3600
          }))
        );
        total = totalHours * 3600;
      }
      setShareCardData({
        totalSeconds: total,
        subjects: subjectList.sort((a, b) => b.seconds - a.seconds),
        date: dateStr,
        username: user?.username || ''
      });
    } else if (cardType === 'achievement') {
      const subjectList = achievementData.map(item => ({
        name: item.subject_name,
        color: item.subject_color,
        plannedSeconds: item.planned_minutes * 60,
        actualSeconds: item.actual_seconds
      }));
      const todoList = todoAchievementData.map(item => ({
        name: item.subject_name,
        color: item.subject_color,
        total_count: item.total_count,
        completed_count: item.completed_count
      }));
      setShareCardData({
        subjects: subjectList,
        todos: todoList,
        date: dateStr,
        username: user?.username || ''
      });
    } else if (cardType === 'goals') {
      const goalList = progress?.goals?.map(g => ({
        name: g.subject_name || t('goals.allSubjects'),
        targetHours: g.target_hours,
        actualSeconds: g.actual_seconds,
        percentage: g.percentage,
        periodType: g.period_type
      })) || [];
      setShareCardData({
        goals: goalList,
        date: dateStr,
        username: user?.username || ''
      });
    } else if (cardType === 'actualStudy') {
      // Group sessions by subject
      const subjectMap = {};
      actualStudySessions.forEach(s => {
        const name = s.study_subject?.subjectname || 'Unknown';
        const color = s.study_subject?.color || '#ccc';
        if (!subjectMap[name]) subjectMap[name] = { name, color, seconds: 0 };
        subjectMap[name].seconds += s.total_seconds || 0;
      });
      const sessions = Object.values(subjectMap).sort((a, b) => b.seconds - a.seconds);
      const todos = completedTodos.map(todo => ({
        name: todo.special_text || todo.study_subject?.subjectname || 'Unknown',
        color: todo.study_subject?.color || '#ccc'
      }));
      setShareCardData({
        sessions,
        todos,
        date: dateStr,
        username: user?.username || ''
      });
    } else if (cardType === 'streak') {
      setShareCardData({
        currentStreak: streak?.current_streak || 0,
        longestStreak: streak?.longest_streak || 0,
        badges: badges || [],
        date: dateStr,
        username: user?.username || ''
      });
    }
    setShareCardType(cardType);
    setShareDialogOpen(true);
  };

  const getAchievementColor = (rate) => {
    if (rate >= 100) return '#4caf50';
    if (rate >= 70) return '#ff9800';
    return '#78909c';
  };

  if (loading || goalLoading || streakLoading) return <div className="statistics-container">Loading...</div>;

  return (
    <div className="statistics-container">
      <h1>{t('statistics.title')}</h1>

      {/* View Mode Toggle + Student Selector */}
      <div className="stats-top-bar">
        <div className="view-mode-toggle">
          <button
            className={viewMode === 'studyTime' ? 'active' : ''}
            onClick={() => setViewMode('studyTime')}
          >
            {t('statistics.plan')}
          </button>
          <button
            className={viewMode === 'achievement' ? 'active' : ''}
            onClick={() => setViewMode('achievement')}
          >
            {t('achievement.title')}
          </button>
          <button
            className={viewMode === 'actualStudy' ? 'active' : ''}
            onClick={() => setViewMode('actualStudy')}
          >
            {t('actualStudy.title')}
          </button>
          <button
            className={viewMode === 'goals' ? 'active' : ''}
            onClick={() => setViewMode('goals')}
          >
            {t('goals.title')}
          </button>
          <button
            className={viewMode === 'streaks' ? 'active' : ''}
            onClick={() => setViewMode('streaks')}
          >
            {t('streak.title')}
          </button>
        </div>
        {myStudents.length > 0 && (
          <select
            value={userId || ''}
            onChange={handleStudentChange}
            className="stats-student-select"
          >
            <option value="">{t('My Statistics')}</option>
            {myStudents.filter(s => s.perm_statistics).map(s => (
              <option key={s.user_id} value={s.user_id}>{s.username}</option>
            ))}
          </select>
        )}
        <div className="stats-export-buttons">
          <button className="pdf-export-btn" onClick={handleExportPdf} disabled={isPdfExporting}>
            {isPdfExporting ? t('pdf.exporting') : t('pdf.export')}
          </button>
          <button className="png-export-btn" onClick={handleExportPng} disabled={isPngExporting}>
            {isPngExporting ? t('png.exporting') : t('png.export')}
          </button>
          <button className="share-export-btn" onClick={handleShareImage}>
            {t('share.share')}
          </button>
        </div>
      </div>

      <div ref={printRef}>
      {viewMode !== 'goals' && viewMode !== 'streaks' && (
        <div className="time-range-selector stats-period-selector">
          <button
            className={timeRange === 'day' ? 'active' : ''}
            onClick={() => setTimeRange('day')}
          >
            {t('statistics.timeRange.daily')}
          </button>
          <button
            className={timeRange === 'week' ? 'active' : ''}
            onClick={() => setTimeRange('week')}
          >
            {t('statistics.timeRange.weekly')}
          </button>
          <button
            className={timeRange === 'month' ? 'active' : ''}
            onClick={() => setTimeRange('month')}
          >
            {t('statistics.timeRange.monthly')}
          </button>
          <button
            className={timeRange === 'all' ? 'active' : ''}
            onClick={() => setTimeRange('all')}
          >
            {t('statistics.timeRange.all')}
          </button>
          <div className="custom-range">
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="date-picker"
            />
            <span>~</span>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="date-picker"
            />
            <button
              className="apply-range"
              onClick={() => {
                if (viewMode === 'achievement') fetchAchievementData('custom');
                else if (viewMode === 'actualStudy') fetchActualStudyData('custom');
                else fetchStudyData('custom');
              }}
            >
              {t('statistics.timeRange.apply')}
            </button>
          </div>
        </div>
      )}

      {viewMode === 'studyTime' && (
        <>
          <div className="statistics-summary">
            <h2>
              {timeRange === 'all'
                ? t('statistics.summary.total')
                : timeRange === 'day'
                  ? t('statistics.summary.today')
                  : timeRange === 'week'
                    ? t('statistics.summary.thisWeek')
                    : t('statistics.summary.thisMonth')}
            </h2>
            <p className="total-hours">{totalHours}{t('statistics.summary.hours')}</p>
            <p className="period-display">{displayPeriod}</p>
            <button className="share-stats-btn" onClick={() => openShareDialog(timeRange === 'week' ? 'weekly' : 'daily')}>
              {t('share.share')}
            </button>

            <div className="category-breakdown-bar">
              {studyData.map((category, index) => {
                const width = totalHours > 0 ? (category.totalHours / totalHours) * 100 : 0;
                if (width === 0) return null;
                return (
                  <div
                    key={index}
                    className="breakdown-segment"
                    style={{
                      width: `${width}%`,
                      backgroundColor: category.color,
                      border: isLightColor(category.color) ? '1px solid #ddd' : 'none'
                    }}
                    title={`${category.name}: ${category.totalHours}h (${width.toFixed(1)}%)`}
                  />
                );
              })}
            </div>
          </div>

          {studyData.length > 0 && (
            <div className="chart-container">
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={studyData}
                    dataKey="totalHours"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    outerRadius={100}
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  >
                    {studyData.map((entry, index) => (
                      <Cell key={index} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => `${value}${t('statistics.summary.hours')}`} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          )}

          <div className="statistics-detail">
            <h3>{t('statistics.detail.byCategory')}</h3>
            {studyData.map((category, catIndex) => (
              <div key={catIndex} className="category-section" style={{ borderLeft: `5px solid ${category.color}` }}>
                <div className="category-header">
                  <div className="category-title-wrapper">
                    <span
                      className="category-color-dot"
                      style={{
                        backgroundColor: category.color,
                        border: isLightColor(category.color) ? '1px solid #ccc' : 'none'
                      }}
                    ></span>
                    <span className="category-title">{category.name}</span>
                  </div>
                  <span className="category-time">{category.totalHours}{t('statistics.summary.hours')}</span>
                </div>
                <div className="category-subjects">
                  {category.subjects.map((item, subIndex) => (
                    <div key={subIndex} className="subject-row">
                      <div className="subject-name">
                        {item.name}
                      </div>
                      <div className="subject-hours">
                        <div
                          className="hours-bar"
                          style={{
                            width: `${totalHours > 0 ? (item.hours / (totalHours * 1.0)) * 100 : 0}%`,
                            backgroundColor: category.color,
                            border: isLightColor(category.color) ? '1px solid #ddd' : 'none'
                          }}
                        ></div>
                        <span>{item.hours}{t('statistics.summary.hours')}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {viewMode === 'achievement' && (() => {
        const totalPlannedSec = achievementData.reduce((sum, item) => sum + item.planned_minutes * 60, 0);
        const totalActualSec = achievementData.reduce((sum, item) => sum + item.actual_seconds, 0);
        const scheduleRate = totalPlannedSec > 0 ? Math.round((totalActualSec / totalPlannedSec) * 100) : 0;
        const totalTodos = todoAchievementData.reduce((sum, item) => sum + item.total_count, 0);
        const completedTodos = todoAchievementData.reduce((sum, item) => sum + item.completed_count, 0);
        const todoRate = totalTodos > 0 ? Math.round((completedTodos / totalTodos) * 100) : 0;
        const hasSchedule = achievementData.length > 0;
        const hasTodo = todoAchievementData.length > 0;
        const overallParts = [hasSchedule ? scheduleRate : null, hasTodo ? todoRate : null].filter(v => v !== null);
        const overallRate = overallParts.length > 0 ? Math.round(overallParts.reduce((a, b) => a + b, 0) / overallParts.length) : 0;
        const stripeStyle = (color) => ({
          background: `repeating-linear-gradient(45deg, rgba(255,255,255,0.25) 0px, rgba(255,255,255,0.25) 3px, transparent 3px, transparent 6px), ${color}`,
        });

        return (
        <div className="achievement-section">
          <p className="period-display">{displayPeriod}</p>
          {(hasSchedule || hasTodo) && (
            <button className="share-stats-btn" onClick={() => openShareDialog('achievement')}>
              {t('share.share')}
            </button>
          )}
          {!hasSchedule && !hasTodo ? (
            <p className="achievement-no-data">{t('achievement.noData')}</p>
          ) : (
            <>
            {/* Overall Achievement */}
            <div className="achievement-overall">
              <h3 className="achievement-section-title">{t('achievement.overall')}</h3>
              <div className="achievement-overall-rate" style={{ color: getAchievementColor(overallRate) }}>
                {overallRate}%
              </div>
              <div className="achievement-overall-bars">
                {hasSchedule && (
                  <div className="achievement-overall-item">
                    <span className="achievement-overall-label">{t('achievement.schedule')}</span>
                    <div className="achievement-bar-wrapper">
                      <div className="achievement-bar achievement-bar-schedule" style={{ width: `${Math.min(scheduleRate, 100)}%` }} />
                    </div>
                    <span className="achievement-overall-value">{scheduleRate}%</span>
                  </div>
                )}
                {hasTodo && (
                  <div className="achievement-overall-item">
                    <span className="achievement-overall-label">{t('achievement.todoTitle')}</span>
                    <div className="achievement-bar-wrapper">
                      <div className="achievement-bar achievement-bar-todo" style={{ width: `${Math.min(todoRate, 100)}%` }} />
                    </div>
                    <span className="achievement-overall-value">{todoRate}%</span>
                  </div>
                )}
              </div>
            </div>

            {/* Schedule Achievement */}
            {hasSchedule && (
              <>
              <h3 className="achievement-section-title">{t('achievement.schedule')}</h3>
              <div className="achievement-grid">
                {achievementData.map((item, index) => {
                  const plannedSeconds = item.planned_minutes * 60;
                  const rate = plannedSeconds > 0 ? Math.round((item.actual_seconds / plannedSeconds) * 100) : (item.actual_seconds > 0 ? 999 : 0);
                  const maxSeconds = Math.max(plannedSeconds, item.actual_seconds);
                  const plannedWidth = maxSeconds > 0 ? (plannedSeconds / maxSeconds) * 100 : 0;
                  const actualWidth = maxSeconds > 0 ? (item.actual_seconds / maxSeconds) * 100 : 0;

                  return (
                    <div key={index} className="achievement-card">
                      <div className="achievement-card-header">
                        <div className="achievement-color-dot" style={{ backgroundColor: item.subject_color }} />
                        <span className="achievement-card-name">{item.subject_name}</span>
                        <span className="achievement-card-rate" style={{ color: getAchievementColor(rate) }}>
                          {rate > 100 ? t('achievement.overAchieved') : `${rate}%`}
                        </span>
                      </div>
                      <div className="achievement-card-detail">
                        <div className="achievement-card-row">
                          <span className="achievement-card-label">{t('achievement.planned')}</span>
                          <div className="achievement-bar-wrapper">
                            <div className="achievement-bar" style={{ width: `${plannedWidth}%`, backgroundColor: item.subject_color }} />
                          </div>
                          <span className="achievement-card-val">{item.planned_minutes}{t('minutes')}</span>
                        </div>
                        <div className="achievement-card-row">
                          <span className="achievement-card-label">{t('achievement.actual')}</span>
                          <div className="achievement-bar-wrapper">
                            <div className="achievement-bar" style={{ width: `${actualWidth}%`, ...stripeStyle(item.subject_color) }} />
                          </div>
                          <span className="achievement-card-val">{Math.floor(item.actual_seconds / 60)}{t('minutes')}</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              </>
            )}

            {/* TODO Achievement */}
            {hasTodo && (
              <>
              <h3 className="achievement-section-title">{t('achievement.todoTitle')}</h3>
              <div className="achievement-grid">
                {todoAchievementData.map((item, index) => {
                  const rate = item.total_count > 0 ? Math.round((item.completed_count / item.total_count) * 100) : 0;

                  return (
                    <div key={index} className="achievement-card">
                      <div className="achievement-card-header">
                        <div className="achievement-color-dot" style={{ backgroundColor: item.subject_color }} />
                        <span className="achievement-card-name">{item.subject_name}</span>
                        <span className="achievement-card-rate" style={{ color: getAchievementColor(rate) }}>
                          {rate}%
                        </span>
                      </div>
                      <div className="achievement-card-detail">
                        <div className="achievement-card-row">
                          <span className="achievement-card-label">{t('achievement.todoDone')}</span>
                          <div className="achievement-bar-wrapper">
                            <div className="achievement-bar" style={{ width: `${rate}%`, backgroundColor: item.subject_color }} />
                          </div>
                          <span className="achievement-card-val">{item.completed_count}/{item.total_count}{t('achievement.todoUnit')}</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              </>
            )}
            </>
          )}
        </div>
        );
      })()}

      {viewMode === 'goals' && (
        <div className="goals-section">
          {/* Period Toggle for Goals */}
          <div className="time-range-selector goal-period-selector">
            <button
              className={goalPeriodType === 'all' ? 'active' : ''}
              onClick={() => handleGoalPeriodChange('all')}
            >
              {t('goals.periodAll')}
            </button>
            <button
              className={goalPeriodType === 'daily' ? 'active' : ''}
              onClick={() => handleGoalPeriodChange('daily')}
            >
              {t('goals.periodDaily')}
            </button>
            <button
              className={goalPeriodType === 'weekly' ? 'active' : ''}
              onClick={() => handleGoalPeriodChange('weekly')}
            >
              {t('goals.periodWeekly')}
            </button>
            <button
              className={goalPeriodType === 'monthly' ? 'active' : ''}
              onClick={() => handleGoalPeriodChange('monthly')}
            >
              {t('goals.periodMonthly')}
            </button>
            <button
              className={goalPeriodType === 'custom' ? 'active' : ''}
              onClick={() => handleGoalPeriodChange('custom')}
            >
              {t('goals.periodCustom')}
            </button>
          </div>

          {/* Period Display */}
          {progress && progress.start_date && progress.end_date && (
            <p className="period-display">
              {new Date(progress.start_date).toLocaleDateString()} ~ {new Date(progress.end_date).toLocaleDateString()}
            </p>
          )}

          {/* Add Goal Button + Share */}
          <div className="goals-header">
            {progress?.goals?.length > 0 && (
              <button className="share-stats-btn" onClick={() => openShareDialog('goals')}>
                {t('share.share')}
              </button>
            )}
            {!isReadOnly && <button className="btn-primary goal-add-btn" onClick={() => openGoalDialog()}>
              + {t('goals.add')}
            </button>}
          </div>

          {/* Goal Progress List */}
          {progress && progress.goals && progress.goals.length > 0 ? (
            <div className="goals-list">
              {progress.goals.map((goal) => {
                const percentage = Math.min(goal.percentage, 100);
                const isAchieved = goal.percentage >= 100;
                const progressColor = getProgressGradient(goal.percentage);

                return (
                  <div key={goal.goal_id} className={`goal-item ${isAchieved ? 'goal-achieved' : ''}`}>
                    <div className="goal-item-header">
                      <div className="goal-item-info">
                        <span className="goal-period-badge">{getPeriodLabel(goal.period_type)}</span>
                        <span className="goal-subject-name">
                          {goal.subject_name || t('goals.allSubjects')}
                        </span>
                        {goal.period_type === 'custom' && goal.start_date && goal.end_date && (
                          <span className="goal-custom-period">
                            {goal.start_date} ~ {goal.end_date}
                          </span>
                        )}
                      </div>
                      {goal.description && (
                        <div className="goal-description">{goal.description}</div>
                      )}
                      {!isReadOnly && (
                        <div className="goal-item-actions">
                          <button
                            className="goal-action-btn goal-edit-btn"
                            onClick={() => openGoalDialog(goal)}
                            title={t('goals.edit')}
                          >
                            &#9998;
                          </button>
                          <button
                            className="goal-action-btn goal-delete-btn"
                            onClick={() => setDeleteConfirmGoalId(goal.goal_id)}
                            title={t('goals.delete')}
                          >
                            &#10005;
                          </button>
                        </div>
                      )}
                    </div>

                    <div className="goal-progress-info">
                      <span className="goal-actual-hours">
                        {(goal.actual_seconds / 3600).toFixed(1)}{t('statistics.summary.hours')}
                      </span>
                      <span className="goal-target-hours">
                        / {goal.target_hours}{t('statistics.summary.hours')}
                      </span>
                      <span className="goal-percentage" style={{ color: progressColor }}>
                        {goal.percentage.toFixed(0)}%
                      </span>
                    </div>

                    <div className="goal-progress-bar-wrapper">
                      <div
                        className="goal-progress-bar"
                        style={{
                          width: `${percentage}%`,
                          backgroundColor: progressColor
                        }}
                      />
                    </div>

                    {isAchieved && (
                      <div className="goal-congrats">
                        {t('goals.congratulations')}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="goals-no-data">{t('goals.noGoals')}</p>
          )}
        </div>
      )}

      {viewMode === 'actualStudy' && (
        <div className="achievement-section">
          <p className="period-display">{displayPeriod}</p>
          {(actualStudySessions.length > 0 || completedTodos.length > 0) && (
            <button className="share-stats-btn" onClick={() => openShareDialog('actualStudy')}>
              {t('share.share')}
            </button>
          )}
          {actualStudySessions.length === 0 && completedTodos.length === 0 ? (
            <p className="achievement-no-data">{t('actualStudy.noData')}</p>
          ) : (() => {
            // Helper: display seconds (modified_seconds if set, else total_seconds)
            const displaySec = (s) => s.modified_seconds != null ? s.modified_seconds : (s.total_seconds || 0);

            // Group timer sessions by subject_id
            const timerGroups = {};
            actualStudySessions.filter(s => !s.auto_generated).forEach(s => {
              const sid = s.subject_id;
              if (!timerGroups[sid]) timerGroups[sid] = { subjectName: s.study_subject?.subjectname || 'Unknown', subjectColor: s.study_subject?.color || '#ccc', totalSeconds: 0, sessions: [] };
              timerGroups[sid].totalSeconds += displaySec(s);
              timerGroups[sid].sessions.push(s);
            });
            const timerGroupList = Object.values(timerGroups).sort((a, b) => b.totalSeconds - a.totalSeconds);
            const timerMaxSec = timerGroupList.length > 0 ? Math.max(...timerGroupList.map(g => g.totalSeconds)) : 1;

            // Group auto-generated sessions by subject_id
            const autoGroups = {};
            actualStudySessions.filter(s => s.auto_generated).forEach(s => {
              const sid = s.subject_id;
              if (!autoGroups[sid]) autoGroups[sid] = { subjectName: s.study_subject?.subjectname || 'Unknown', subjectColor: s.study_subject?.color || '#ccc', totalSeconds: 0, sessions: [] };
              autoGroups[sid].totalSeconds += s.total_seconds || 0;
              autoGroups[sid].sessions.push(s);
            });
            const autoGroupList = Object.values(autoGroups).sort((a, b) => b.totalSeconds - a.totalSeconds);
            const autoMaxSec = autoGroupList.length > 0 ? Math.max(...autoGroupList.map(g => g.totalSeconds)) : 1;

            // Group completed TODOs by subject_id
            const todoGroups = {};
            completedTodos.forEach(todo => {
              const sid = todo.subject_id;
              if (!todoGroups[sid]) todoGroups[sid] = { subjectName: todo.study_subject?.subjectname || 'Unknown', subjectColor: todo.study_subject?.color || '#ccc', todos: [] };
              todoGroups[sid].todos.push(todo);
            });
            const todoGroupList = Object.values(todoGroups).sort((a, b) => b.todos.length - a.todos.length);

            const fmtTime = (sec) => { const h = Math.floor(sec / 3600); const m = Math.floor((sec % 3600) / 60); return h > 0 ? `${h}h ${m}m` : `${m}m`; };
            const fmtDate = (dt) => { const d = new Date(dt); return `${String(d.getMonth() + 1).padStart(2, '0')}.${String(d.getDate()).padStart(2, '0')} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`; };

            return (
            <>
              {/* Timer Sessions (Real-time / Auto) */}
              {timerGroupList.length > 0 && (
                <>
                  <h3 className="achievement-section-title">{t('actualStudy.timerSession')}</h3>
                  <div className="achievement-grid">
                    {timerGroupList.map(group => (
                      <div key={group.subjectName} className="achievement-card">
                        <div className="achievement-card-header">
                          <div className="achievement-color-dot" style={{ backgroundColor: group.subjectColor }} />
                          <span className="achievement-card-name">{group.subjectName}</span>
                          <span className="achievement-card-rate">{fmtTime(group.totalSeconds)}</span>
                        </div>
                        <div className="achievement-card-detail">
                          <div className="achievement-card-row" style={{ marginBottom: 4 }}>
                            <span className="achievement-card-label">{group.sessions.length}{t('achievement.todoUnit')}</span>
                            <div className="achievement-bar-wrapper">
                              <div className="achievement-bar" style={{ width: `${(group.totalSeconds / timerMaxSec) * 100}%`, backgroundColor: group.subjectColor }} />
                            </div>
                            <span className="achievement-card-val">{fmtTime(group.totalSeconds)}</span>
                          </div>
                          {group.sessions.map(session => (
                            <div key={session.session_id} className="achievement-card-row actual-study-sub-row">
                              <span className="actual-study-badge badge-timer">{t('actualStudy.timerBadge')}</span>
                              <span className="achievement-card-label">{fmtDate(session.start_time)}</span>
                              {editingSessionId === session.session_id ? (
                                <span className="session-edit-inline">
                                  <input type="number" min="0" max="23" value={editHours} onChange={e => setEditHours(e.target.value)} className="session-edit-input" />
                                  <span>h</span>
                                  <input type="number" min="0" max="59" value={editMinutes} onChange={e => setEditMinutes(e.target.value)} className="session-edit-input" />
                                  <span>m</span>
                                  <button className="session-edit-save-btn" onClick={() => saveEditSession(session.session_id)} title={t('save')}>&#10003;</button>
                                  {session.modified_seconds != null && (
                                    <button className="session-edit-reset-btn" onClick={() => resetEditSession(session.session_id)} title={t('actualStudy.resetTime')}>&#8634;</button>
                                  )}
                                  <button className="session-edit-cancel-btn" onClick={cancelEditSession} title={t('Cancel')}>&#10005;</button>
                                </span>
                              ) : (
                                <>
                                  <span className={`achievement-card-val${session.modified_seconds != null ? ' session-modified' : ''}`}>{fmtTime(displaySec(session))}</span>
                                  {!isReadOnly && <button className="session-edit-btn" onClick={() => startEditSession(session)} title={t('actualStudy.editTime')}>&#9998;</button>}
                                </>
                              )}
                              {editingSessionId !== session.session_id && !isReadOnly && <button className="actual-study-delete-btn" onClick={() => setDeleteSessionConfirm({ type: 'timer', session })} title={t('delete')}>
                                &#10005;
                              </button>}
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}

              {/* Auto-generated (Plan Completed / Manual) */}
              {autoGroupList.length > 0 && (
                <>
                  <h3 className="achievement-section-title">{t('actualStudy.autoGenerated')}</h3>
                  <div className="achievement-grid">
                    {autoGroupList.map(group => (
                      <div key={group.subjectName} className="achievement-card">
                        <div className="achievement-card-header">
                          <div className="achievement-color-dot" style={{ backgroundColor: group.subjectColor }} />
                          <span className="achievement-card-name">{group.subjectName}</span>
                          <span className="achievement-card-rate">{fmtTime(group.totalSeconds)}</span>
                        </div>
                        <div className="achievement-card-detail">
                          <div className="achievement-card-row" style={{ marginBottom: 4 }}>
                            <span className="achievement-card-label">{group.sessions.length}{t('achievement.todoUnit')}</span>
                            <div className="achievement-bar-wrapper">
                              <div className="achievement-bar" style={{ width: `${(group.totalSeconds / autoMaxSec) * 100}%`, backgroundColor: group.subjectColor, opacity: 0.6 }} />
                            </div>
                            <span className="achievement-card-val">{fmtTime(group.totalSeconds)}</span>
                          </div>
                          {group.sessions.map(session => (
                            <div key={session.session_id} className="achievement-card-row actual-study-sub-row">
                              <span className="actual-study-badge badge-checkbox">{t('actualStudy.checkboxBadge')}</span>
                              <span className="achievement-card-label">{session.time_table?.special_text || group.subjectName}</span>
                              <span className="achievement-card-val">{fmtTime(session.total_seconds)}</span>
                              {!isReadOnly && <button className="actual-study-delete-btn" onClick={() => setDeleteSessionConfirm({ type: 'auto', session })} title={t('delete')}>
                                &#10005;
                              </button>}
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}

              {/* Completed TODOs */}
              {todoGroupList.length > 0 && (
                <>
                  <h3 className="achievement-section-title">{t('actualStudy.completedTodo')}</h3>
                  <div className="achievement-grid">
                    {todoGroupList.map(group => (
                      <div key={group.subjectName} className="achievement-card">
                        <div className="achievement-card-header">
                          <div className="achievement-color-dot" style={{ backgroundColor: group.subjectColor }} />
                          <span className="achievement-card-name">{group.subjectName}</span>
                          <span className="achievement-card-rate">{group.todos.length}{t('achievement.todoUnit')}</span>
                        </div>
                        <div className="achievement-card-detail">
                          {group.todos.map(todo => (
                            <div key={todo.schedule_id} className="achievement-card-row actual-study-sub-row">
                              <span className="achievement-card-label">{todo.special_text || group.subjectName}</span>
                              <span className="achievement-card-val" />
                              {!isReadOnly && <button className="actual-study-delete-btn" onClick={() => setDeleteSessionConfirm({ type: 'todo', todo })} title={t('delete')}>
                                &#10005;
                              </button>}
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </>
            );
          })()}
        </div>
      )}

      {/* Delete Session Confirmation */}
      <ConfirmationDialog
        isOpen={!!deleteSessionConfirm}
        title={t('Confirm Deletion')}
        message={deleteSessionConfirm?.type === 'auto' ? t('actualStudy.deleteAutoConfirm') : t('actualStudy.deleteConfirm')}
        onConfirm={() => {
          if (!deleteSessionConfirm) return;
          if (deleteSessionConfirm.type === 'todo') {
            handleUndoTodoComplete(deleteSessionConfirm.todo);
            setDeleteSessionConfirm(null);
          } else {
            handleDeleteSession(deleteSessionConfirm.session);
          }
        }}
        onCancel={() => setDeleteSessionConfirm(null)}
        confirmLabel={t('delete')}
        isDanger={true}
      />

      {viewMode === 'streaks' && (
        <div className="streak-section">
          <div className="streak-display">
            <span className="streak-fire-icon">{'\uD83D\uDD25'}</span>
            <span className="streak-current-number">{streak?.current_streak || 0}</span>
            <span className="streak-current-label">{t('streak.days')}</span>
          </div>
          <div className="streak-best">
            {t('streak.bestStreak')}: {streak?.longest_streak || 0}{t('streak.days')}
          </div>
          <button className="share-stats-btn" onClick={() => openShareDialog('streak')}>
            {t('share.share')}
          </button>

          <h3 className="badge-section-title">{t('streak.badges')}</h3>
          {BADGE_DEFINITIONS.length > 0 ? (
            <div className="badge-grid">
              {BADGE_DEFINITIONS.map((def) => {
                const earned = badges.find((b) => b.badge_key === def.badge_key);
                return (
                  <div key={def.badge_key} className={`badge-card ${earned ? 'earned' : 'locked'}`}>
                    <div className="badge-icon">{def.icon}</div>
                    <div className="badge-name">{t(def.nameKey)}</div>
                    <div className="badge-desc">{t(def.descKey)}</div>
                    <div className="badge-date">
                      {earned
                        ? `${t('streak.earned')}: ${new Date(earned.earned_at).toLocaleDateString()}`
                        : t('streak.locked')}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="streak-no-badges">{t('streak.noBadges')}</p>
          )}
        </div>
      )}

      {/* Goal Add/Edit Dialog */}
      {goalDialogOpen && (
        <div className="dialog-overlay" onClick={closeGoalDialog}>
          <div className="dialog-box" onClick={(e) => e.stopPropagation()}>
            <h2 className="dialog-title">
              {editingGoal ? t('goals.edit') : t('goals.add')}
            </h2>

            {!editingGoal && (
              <div className="dialog-field">
                <label className="dialog-label">{t('goals.periodType')}</label>
                <select
                  className="dialog-input"
                  value={goalForm.period_type}
                  onChange={(e) => setGoalForm({ ...goalForm, period_type: e.target.value })}
                >
                  <option value="daily">{t('goals.periodDaily')}</option>
                  <option value="weekly">{t('goals.periodWeekly')}</option>
                  <option value="monthly">{t('goals.periodMonthly')}</option>
                  <option value="custom">{t('goals.periodCustom')}</option>
                </select>
              </div>
            )}

            {goalForm.period_type === 'custom' && !editingGoal && (
              <div className="dialog-field goal-date-fields">
                <div className="goal-date-row">
                  <div className="goal-date-input">
                    <label className="dialog-label">{t('goals.startDate')}</label>
                    <input
                      className="dialog-input"
                      type="date"
                      value={goalForm.start_date}
                      onChange={(e) => setGoalForm({ ...goalForm, start_date: e.target.value })}
                    />
                  </div>
                  <div className="goal-date-input">
                    <label className="dialog-label">{t('goals.endDate')}</label>
                    <input
                      className="dialog-input"
                      type="date"
                      value={goalForm.end_date}
                      onChange={(e) => setGoalForm({ ...goalForm, end_date: e.target.value })}
                    />
                  </div>
                </div>
              </div>
            )}

            <div className="dialog-field">
              <label className="dialog-label">{t('goals.targetHours')}</label>
              <input
                className="dialog-input"
                type="number"
                min="0.5"
                step="0.5"
                value={goalForm.target_hours}
                onChange={(e) => setGoalForm({ ...goalForm, target_hours: e.target.value })}
                placeholder={t('goals.targetHours')}
              />
            </div>

            {!editingGoal && (
              <div className="dialog-field">
                <label className="dialog-label">{t('goals.selectSubject')}</label>
                <select
                  className="dialog-input"
                  value={goalForm.subject_id}
                  onChange={(e) => setGoalForm({ ...goalForm, subject_id: e.target.value })}
                >
                  <option value="">{t('goals.allSubjects')}</option>
                  {subjects.map((sub) => (
                    <option key={sub.subject_id} value={sub.subject_id}>
                      {sub.subjectname}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div className="dialog-field">
              <label className="dialog-label">{t('goals.description')}</label>
              <textarea
                className="dialog-input goal-description-input"
                value={goalForm.description}
                onChange={(e) => setGoalForm({ ...goalForm, description: e.target.value })}
                placeholder={t('goals.descriptionPlaceholder')}
                maxLength={200}
                rows={2}
              />
            </div>

            <div className="dialog-actions">
              <button className="btn-secondary" onClick={closeGoalDialog}>
                {t('cancel')}
              </button>
              <button className="btn-primary" onClick={handleGoalSubmit}>
                {t('save')}
              </button>
            </div>
          </div>
        </div>
      )}

      </div>{/* end printRef */}

      {/* PDF Preview Dialog */}
      {pdfPreview && (
        <div className="dialog-overlay" onClick={() => setPdfPreview(null)}>
          <div className="pdf-preview-dialog" onClick={(e) => e.stopPropagation()}>
            <h3 className="dialog-title">{t('pdf.preview')}</h3>
            <div className="pdf-preview-image-container">
              <img src={pdfPreview.imgDataUrl} alt="PDF Preview" className="pdf-preview-image" />
            </div>
            <p className="pdf-preview-filename">{pdfPreview.fileName}</p>
            <div className="dialog-actions">
              <button className="btn-secondary" onClick={() => setPdfPreview(null)}>{t('cancel')}</button>
              <button className="btn-primary" onClick={handlePdfSave}>{t('pdf.save')}</button>
            </div>
          </div>
        </div>
      )}

      {/* PNG Preview Dialog */}
      {pngPreview && (
        <div className="dialog-overlay" onClick={() => setPngPreview(null)}>
          <div className="pdf-preview-dialog" onClick={(e) => e.stopPropagation()}>
            <h3 className="dialog-title">{t('png.preview')}</h3>
            <div className="pdf-preview-image-container">
              <img src={pngPreview.imgDataUrl} alt="PNG Preview" className="pdf-preview-image" />
            </div>
            <p className="pdf-preview-filename">{pngPreview.fileName}</p>
            <div className="dialog-actions">
              <button className="btn-secondary" onClick={() => setPngPreview(null)}>{t('cancel')}</button>
              <button className="btn-primary" onClick={handlePngSave}>{t('png.save')}</button>
            </div>
          </div>
        </div>
      )}

      {/* Share Dialog */}
      <ShareDialog
        open={shareDialogOpen}
        onClose={() => setShareDialogOpen(false)}
        cardType={shareCardType}
        data={shareCardData}
      />

      {/* Delete Confirmation Dialog */}
      {deleteConfirmGoalId && (
        <div className="dialog-overlay" onClick={() => setDeleteConfirmGoalId(null)}>
          <div className="dialog-box" onClick={(e) => e.stopPropagation()}>
            <h2 className="dialog-title">{t('goals.delete')}</h2>
            <p style={{ textAlign: 'center', color: 'var(--text-secondary)' }}>{t('goals.deleteConfirm')}</p>
            <div className="dialog-actions">
              <button className="btn-secondary" onClick={() => setDeleteConfirmGoalId(null)}>
                {t('cancel')}
              </button>
              <button
                className="btn-primary"
                style={{ backgroundColor: '#f44336' }}
                onClick={() => handleDeleteGoal(deleteConfirmGoalId)}
              >
                {t('delete')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Statistics;
